import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../context/AuthContext';
import api from '../utils/api';
import './FAQPage.css';

const FAQPage = () => {
  const { currentUser } = useContext(AuthContext);
  const [posts, setPosts] = useState([]);
  const [tags, setTags] = useState([]);
  const [selectedTag, setSelectedTag] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openId, setOpenId] = useState(null); // accordion open item

  useEffect(() => {
    fetchTags();
  }, []);

  useEffect(() => {
    fetchFAQ();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTag, searchTerm]);

  const fetchTags = async () => {
    try {
      const res = await api.get('/api/faq/tags');
      setTags(res.data || []);
    } catch {
      // non-critical
    }
  };

  const fetchFAQ = async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({ limit: 100, sortBy: 'publishDate', sortOrder: 'asc' });
      if (selectedTag) params.append('tags', selectedTag);
      if (searchTerm) params.append('search', searchTerm);

      const res = await api.get(`/api/faq/public?${params.toString()}`);
      setPosts(res.data.posts || []);
    } catch (err) {
      setError('Failed to load FAQ. Please try again.');
      console.error('FAQ fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchFAQ();
  };

  const toggle = (id) => setOpenId(prev => (prev === id ? null : id));

  const getPriorityClass = (priority) => {
    const map = { urgent: 'faq-urgent', high: 'faq-high', normal: '', low: 'faq-low' };
    return map[priority] || '';
  };

  // Group by tags if no filter active
  const grouped = React.useMemo(() => {
    if (selectedTag || searchTerm) return null; // flat list when filtering
    const groups = {};
    posts.forEach(post => {
      const tag = post.tags && post.tags.length > 0 ? post.tags[0] : 'General';
      if (!groups[tag]) groups[tag] = [];
      groups[tag].push(post);
    });
    return groups;
  }, [posts, selectedTag, searchTerm]);

  const renderAccordionItem = (post) => (
    <div
      key={post._id}
      className={`faq-item ${openId === post._id ? 'faq-open' : ''} ${getPriorityClass(post.priority)}`}
    >
      <button
        className="faq-question"
        onClick={() => toggle(post._id)}
        aria-expanded={openId === post._id}
      >
        <span className="faq-question-text">{post.title}</span>
        <span className="faq-chevron">{openId === post._id ? '▲' : '▼'}</span>
      </button>
      {openId === post._id && (
        <div className="faq-answer">
          <div
            className="faq-content"
            dangerouslySetInnerHTML={{ __html: post.content }}
          />
          {post.tags && post.tags.length > 1 && (
            <div className="faq-tags">
              {post.tags.map(t => (
                <button key={t} className="faq-tag-chip" onClick={() => { setSelectedTag(t); setOpenId(null); }}>
                  {t}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );

  return (
    <div className="faq-page">
      <div className="faq-header">
        <h1>Frequently Asked Questions</h1>
        <p>Find answers to common questions about our programs and services.</p>
      </div>

      {/* Search & filter bar */}
      <div className="faq-controls">
        <form className="faq-search-form" onSubmit={handleSearch}>
          <input
            type="text"
            className="faq-search-input"
            placeholder="Search questions..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
          <button type="submit" className="faq-search-btn">Search</button>
          {(searchTerm || selectedTag) && (
            <button
              type="button"
              className="faq-clear-btn"
              onClick={() => { setSearchTerm(''); setSelectedTag(''); }}
            >
              Clear
            </button>
          )}
        </form>

        {tags.length > 0 && (
          <div className="faq-tag-filters">
            <button
              className={`faq-tag-filter ${selectedTag === '' ? 'active' : ''}`}
              onClick={() => setSelectedTag('')}
            >
              All Topics
            </button>
            {tags.map(tag => (
              <button
                key={tag}
                className={`faq-tag-filter ${selectedTag === tag ? 'active' : ''}`}
                onClick={() => setSelectedTag(tag)}
              >
                {tag}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Content */}
      {loading ? (
        <div className="faq-loading">
          <div className="spinner" />
          <p>Loading FAQ...</p>
        </div>
      ) : error ? (
        <div className="faq-error">{error}</div>
      ) : posts.length === 0 ? (
        <div className="faq-empty">
          <p>No FAQ entries found{searchTerm || selectedTag ? ' matching your search' : ''}.</p>
        </div>
      ) : grouped ? (
        // Grouped accordion
        Object.entries(grouped).map(([group, items]) => (
          <section key={group} className="faq-group">
            <h2 className="faq-group-title">{group.charAt(0).toUpperCase() + group.slice(1)}</h2>
            <div className="faq-list">{items.map(renderAccordionItem)}</div>
          </section>
        ))
      ) : (
        // Flat list when filtering
        <div className="faq-list">{posts.map(renderAccordionItem)}</div>
      )}

      {!currentUser && (
        <div className="faq-register-cta">
          <p>
            Have more questions? <a href="/register">Create an account</a> or{' '}
            <a href="/login">log in</a> to access member-exclusive resources.
          </p>
        </div>
      )}
    </div>
  );
};

export default FAQPage;
